import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

export const OP_STATUSES = [
  'ON_MOVING',
  'OFF',
  'ON_STOPPED',
  'IDLE_10M',
  'OUT_12H',
  'OUT_24H',
  'OUT_72H',
  'NO_DATA',
];

const MINUTE = 60 * 1000;

const getPositionTimeMs = (position) => {
  const raw = position?.fixTime
    ?? position?.deviceTime
    ?? position?.serverTime
    ?? position?.time
    ?? position?.timestamp;

  const t = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(t) ? t : null;
};

export const getOperationalStatus = (device, position) => {
  const lastUpdate = position?.fixTime
    || position?.deviceTime
    || position?.serverTime
    || position?.time
    || position?.timestamp
    || device?.lastUpdate;
  if (!lastUpdate) return 'NO_DATA';

  const hours = dayjs().diff(dayjs(lastUpdate), 'hour');

  if (hours >= 72) return 'OUT_72H';
  if (hours >= 24) return 'OUT_24H';
  if (hours >= 12) return 'OUT_12H';

  if (!position) return 'NO_DATA';

  const ignition = position?.attributes?.ignition ?? null;
  const speedKph = position?.speed != null ? Number(position.speed) * 1.852 : null;
  const moving = speedKph != null && speedKph > 2;

  const posTime = getPositionTimeMs(position);
  const ageMs = posTime ? (Date.now() - posTime) : null;
  const idle10m = ignition === true && !moving && ageMs != null && ageMs >= 10 * MINUTE;

  if (ignition === false) return 'OFF';
  if (moving) return 'ON_MOVING';
  if (idle10m) return 'IDLE_10M';
  if (ignition === true) return 'ON_STOPPED';

  return 'NO_DATA';
};

export default function useFilter(
  keyword,
  filter,
  filterSort,
  filterMap,
  positions,
  setFilteredDevices,
  setFilteredPositions,
) {
  const groups = useSelector((state) => state?.groups?.items ?? {});
  const devices = useSelector((state) => state?.devices?.items ?? {});
  const selectedId = useSelector((state) => state?.devices?.selectedId ?? null);

  useEffect(() => {
    const deviceGroups = (device) => {
      const groupIds = [];
      let groupId = device?.groupId;

      while (groupId) {
        groupIds.push(groupId);
        groupId = groups[groupId]?.groupId || 0;
      }

      return groupIds;
    };

    const lowerCaseKeyword = (keyword || '').toLowerCase();

    const filtered = Object.values(devices)
      .filter(Boolean)
      .filter((device) => {
        const selectedStatuses = filter?.statuses || [];
        if (!selectedStatuses.length) return true;

        const validStatuses = selectedStatuses.filter((s) => OP_STATUSES.includes(s));
        if (!validStatuses.length) return true;

        const position = positions?.[device.id];
        const opStatus = getOperationalStatus(device, position);
        return validStatuses.includes(opStatus);
      })
      .filter((device) => {
        const selectedGroups = filter?.groups || [];
        if (!selectedGroups.length) return true;
        return deviceGroups(device).some((id) => selectedGroups.includes(id));
      })
      .filter((device) => {
        if (!lowerCaseKeyword) return true;

        return [
          device?.name,
          device?.uniqueId,
          device?.phone,
          device?.model,
          device?.contact,
          device?.category,
        ].some((value) => (
          value && String(value).toLowerCase().includes(lowerCaseKeyword)
        ));
      });

    switch (filterSort) {
      case 'name':
        filtered.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
        break;

      case 'lastUpdate':
        filtered.sort((a, b) => {
          const timeA = a?.lastUpdate ? dayjs(a.lastUpdate).valueOf() : 0;
          const timeB = b?.lastUpdate ? dayjs(b.lastUpdate).valueOf() : 0;
          return timeB - timeA;
        });
        break;

      default:
        break;
    }

    setFilteredDevices(filtered);

    const nextPositions = selectedId
      ? [positions?.[selectedId]].filter(Boolean)
      : filterMap
        ? filtered.map((device) => positions?.[device.id]).filter(Boolean)
        : Object.values(positions || {});

    setFilteredPositions(nextPositions);
  }, [
    keyword,
    filter,
    filterSort,
    filterMap,
    groups,
    devices,
    positions,
    selectedId,
    setFilteredDevices,
    setFilteredPositions,
  ]);
}
